import Analytics from '@web-solutions/module-analytics';
import { PaymentSystem, ProductInfo } from '@web-solutions/react-billing';
import { Currency } from '@web-solutions/react-billing/constants';
import { ProductConfig } from 'core/constants/remote-config';
import { usePayment } from 'core/payment/payment-processor-provider/paymentProcessorContext';
import { setActiveProduct } from 'core/store/billing/actions';
import { selectPaymentSystem } from 'core/store/billing/selectors';
import { useAppSelector } from 'core/store/hooks';
import { nextStep, setIsStartUpsaleFlow } from 'core/upsale/store';
import { handlePurchaseThunk, changePurchaseTokenIdThunk } from 'core/upsale/store/thunks/purchase';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface Props {
  product: ProductInfo & ProductConfig;
}

export const useUpsaleMethods = ({ product }: Props) => {
  const dispatch = useDispatch();
  const isStartUpsaleFlow = useAppSelector((state) => state.upsale.isStartUpsaleFlow);
  const handleNext = () => dispatch(nextStep());
  const { ppRef, otpRef } = usePayment();
  const paymentType = useSelector(selectPaymentSystem);
  const [tokenThreeDSecure, setTokenThreeDSecure] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<any>(null);

  const handleActiveProduct = () => {
    dispatch(setActiveProduct(product));
  };

  const handleRecurlyClick = async (isTest?: boolean) => {
    if (isTest) {
      dispatch(changePurchaseTokenIdThunk('test'));
    } else {
      const result = await dispatch(
        handlePurchaseThunk({ ...product, ref: ppRef, handleNext, setAttempt })
      );

      //@ts-ignore
      if (result.payload) {
        //@ts-ignore
        const tokenId = result?.payload?.data?.three_d_secure_action_token_id;

        if (tokenId) {
          setTokenThreeDSecure(tokenId as any);
        }
      }
    }
  };

  const handleOneTimeClick = async (isTest?: boolean) => {
    return otpRef.current?.purchase({
      amount: +(product?.amount || 0),
      currency: !isTest ? (product?.currency as Currency) : ('test error purchase' as Currency),
      productCode: product?.productCode || 'productCode',
      productId: product?.id || 'no product id',
    });
  };

  const handleClick = async (isTest?: boolean) => {
    Analytics.trackEvent('upsale', 'purchase_click', {
      status: product?.isDiscount ? 'discount' : 'main',
      product: product.productCode,
      amount: product.amount,
    });

    switch (paymentType) {
      case PaymentSystem.RECURLY:
        return handleRecurlyClick(isTest);
      case PaymentSystem.SOLIDGATE:
        return handleOneTimeClick(isTest);
      case PaymentSystem.TRUEGATE:
        return handleOneTimeClick(isTest);
      case PaymentSystem.STRIPE:
        return handleOneTimeClick(isTest);
    }
  };

  const handleClose = (screen?: string, action?: string, delay?: number) => {
    Analytics.trackEvent('upsale', 'closed', {
      status: product?.isDiscount ? 'discount' : 'main',
      action: action,
      delay: delay,
      ...(screen
        ? { screen }
        : {
            product: product.productCode,
            amount: product.amount,
          }),
    });
  };

  const handleOpen = useCallback(
    (screen?: string) => {
      Analytics.trackEvent('upsale', 'shown', {
        status: product?.isDiscount ? 'discount' : 'main',
        ...(screen
          ? { screen }
          : {
              product: product.productCode,
              amount: product.amount,
            }),
      });
      if (!isStartUpsaleFlow) {
        dispatch(setIsStartUpsaleFlow(true));
      }
    },
    [product]
  );

  return {
    handleClick,
    handleActiveProduct,
    handleClose,
    handleOpen,
    handleNext,
    tokenThreeDSecure,
    setTokenThreeDSecure,
    ppRef,
    attempt,
    setAttempt,
  };
};
